import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';

import { firstValueFrom } from 'rxjs';

import { notifyAndTrackError } from '../../../../common/error';
import { successMsg, warningMsg } from '../../../../common/messages';

import { AppInsightsService } from '../../../../services/app-insights.service';
import { CatalogAdminService } from '../../../../services/insecta';
import { BartenderConfiguration, ICatalogUpdateRequest } from '../../../../services/insecta/insecta.models';

@Component({
  selector: 'tab-printing',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ButtonModule, CardModule, DialogModule, FloatLabelModule, InputTextModule, TooltipModule],
  templateUrl: './tab-printing.component.html'
})
export class TabPrintingComponent implements OnDestroy, OnInit {
  @Input() catalog: any;
  @Input() catalogId: string = '';
  @Input() locationId: string = '';
  @Input() siteCode: string = '';
  @Input() currentUser: string = '';
  @Output() isReady = new EventEmitter<boolean>();

  printerList: string[] = [];
  showAddPrinterDialog = false;
  newPrinterName = '';
  labelTemplateList: string[] = [];
  showAddLabelDialog = false;
  newLabelTemplatePath = '';
  bartenderUrl: string = '';
  isBartenderEditing = false;
  editBartenderUrl: string = '';

  libraryId: string = '';
  isLibraryIdEditing = false;
  editLibraryId: string = '';

  isLoading = false;
  pageVars = { isDirty: false, isEditing: false, isReady: false };

  constructor(
    private catalogService: CatalogAdminService,
    private msgService: MessageService,
    private appInsights: AppInsightsService
  ) {}

  ngOnInit(): void {
    this.isReady.emit(true);
    this.loadFromCatalog();
  }

  ngOnChanges(): void {
    this.loadFromCatalog();
  }

  ngOnDestroy() {}

  loadFromCatalog() {
    console.log(this.catalog);
    this.bartenderUrl = this.catalog?.bartenderConfig?.serverUrl || '';
    this.libraryId = this.catalog?.bartenderConfig?.libraryId || '';
    this.printerList = this.catalog?.bartenderConfig?.printers || [];
    this.labelTemplateList = this.catalog?.bartenderConfig?.labels || [];

    this.pageVars.isReady = true;
  }

  loadBartenderData() {
    this.pageVars.isReady = true;
  }
  onEditBartenderUrl() {
    this.isBartenderEditing = true;
    this.editBartenderUrl = this.bartenderUrl;
  }

  onCancelBartenderUrl() {
    this.isBartenderEditing = false;
    this.editBartenderUrl = this.bartenderUrl;
  }

  onApplyBartenderUrl() {
    this.bartenderUrl = this.editBartenderUrl.trim();
    this.isBartenderEditing = false;
    this.updateDirtyState();
  }
  onEditLibraryId() {
    this.isLibraryIdEditing = true;
    this.editLibraryId = this.libraryId;
  }

  onCancelLibraryId() {
    this.isLibraryIdEditing = false;
    this.editLibraryId = this.libraryId;
  }

  onApplyLibraryId() {
    const trimmed = this.editLibraryId.trim();

    if (!trimmed) {
      warningMsg(this.msgService, 'Library ID cannot be empty');
      return;
    }

    if (!this.isValidGuid(trimmed)) {
      warningMsg(this.msgService, 'Library ID must be a valid GUID format');
      return;
    }

    this.libraryId = trimmed;
    this.isLibraryIdEditing = false;
    this.updateDirtyState();
    successMsg(this.msgService, 'Library ID updated');
  }
  private isValidGuid(guid: string): boolean {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(guid);
  }
  openAddPrinterDialog() {
    this.showAddPrinterDialog = true;
    this.newPrinterName = '';
  }

  closeAddPrinterDialog() {
    this.showAddPrinterDialog = false;
    this.newPrinterName = '';
  }

  addPrinter() {
    const trimmedName = this.newPrinterName.trim();

    if (!trimmedName) {
      warningMsg(this.msgService, 'Printer name cannot be empty');
      return;
    }

    if (this.printerList.includes(trimmedName)) {
      warningMsg(this.msgService, 'This printer already exists');
      return;
    }

    this.printerList.push(trimmedName);
    this.updateDirtyState();
    this.closeAddPrinterDialog();
    successMsg(this.msgService, `Printer "${trimmedName}" added successfully`);
  }

  removePrinter(printerName: string) {
    const index = this.printerList.indexOf(printerName);
    if (index > -1) {
      this.printerList.splice(index, 1);
      this.updateDirtyState();
      successMsg(this.msgService, `Printer "${printerName}" removed`);
    }
  }

  openAddLabelDialog() {
    this.showAddLabelDialog = true;
    this.newLabelTemplatePath = '';
  }

  closeAddLabelDialog() {
    this.showAddLabelDialog = false;
    this.newLabelTemplatePath = '';
  }
  private isLabelPathDuplicate(newPath: string): boolean {
    const normalizedNewPath = newPath.trim().toLowerCase();
    return this.labelTemplateList.some((existingLabel) => existingLabel.trim().toLowerCase() === normalizedNewPath);
  }

  addLabelTemplate() {
    const trimmedPath = this.newLabelTemplatePath.trim();

    if (!trimmedPath) {
      warningMsg(this.msgService, 'Label template path cannot be empty');
      return;
    }

    if (this.isLabelPathDuplicate(trimmedPath)) {
      warningMsg(this.msgService, 'This label template path already exists');
      return;
    }

    this.labelTemplateList.push(trimmedPath);
    this.updateDirtyState();
    this.closeAddLabelDialog();
    successMsg(this.msgService, `Label template "${trimmedPath}" added successfully`);
  }

  removeLabelTemplate(labelPath: string) {
    const index = this.labelTemplateList.indexOf(labelPath);
    if (index > -1) {
      this.labelTemplateList.splice(index, 1);
      this.updateDirtyState();
      successMsg(this.msgService, `Label template "${labelPath}" removed`);
    }
  }

  savePrintingConfiguration() {
    if (!this.bartenderUrl.trim()) {
      warningMsg(this.msgService, 'Bartender URL is required');
      return;
    }

    if (!this.libraryId.trim()) {
      warningMsg(this.msgService, 'Library ID is required');
      return;
    }

    if (!this.isValidGuid(this.libraryId)) {
      warningMsg(this.msgService, 'Library ID must be a valid GUID format');
      return;
    }

    const bartenderConfig: BartenderConfiguration = {
      serverUrl: this.bartenderUrl,
      libraryId: this.libraryId,
      printers: [...this.printerList],
      labels: [...this.labelTemplateList]
    };

    const request: ICatalogUpdateRequest = {
      id: this.catalogId,
      locationId: this.locationId,
      siteCode: this.siteCode,
      BartenderConfig: bartenderConfig,
      actionBy: this.currentUser
    };

    this.isLoading = true;

    firstValueFrom(this.catalogService.updateCatalog(request))
      .then(() => {
        this.pageVars.isDirty = false;
        this.pageVars.isEditing = false;
        successMsg(this.msgService, 'Printing configuration has been updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: 'TabPrintingComponent',
          action: 'SavePrintingConfiguration:Error'
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  updateDirtyState() {
    this.pageVars.isDirty = true;
  }

  hasUnsavedChanges(): boolean {
    return this.pageVars.isDirty;
  }
}
